import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import gratificacionService from '../services/gratificacionService';
import CerrarGratificacionConfirmModal from './CerrarGratificacionConfirmModal';
import { formatFechaSoloDiaDdMmYyyy } from '../utils/formatFechaSoloDia';
import { useAuth } from '../hooks/useAuth';
import { obtenerFirmaEmpresaBase64 } from '../services/firmaEmpresaService';
import {
  descargarConstanciaGratificacionIndividual,
  exportarTodasConstanciasGratificacion,
} from '../utils/gratificacionConstanciaPdf';

/** Clave estable del detalle: evita perder empleados al cerrar (el padre usa id numérico y el detalle `grat-{id}`). */
const clavePeriodoGratificacion = (periodo) => {
  if (!periodo) return null;
  const gid = periodo.gratificacionId ?? (typeof periodo.id === 'number' ? periodo.id : null);
  if (gid != null && String(periodo.id || '').startsWith('grat-')) return String(periodo.id);
  if (gid != null) return `grat-${gid}`;
  return periodo.id != null ? String(periodo.id) : null;
};

/** Mapear fila de API al formato del componente (empleado, cargo, montos, datos para constancia). */
const mapApiFilaToDetalle = (row) => ({
  id: row.id,
  empleado: row.empleado,
  documento: row.documento || '',
  cargo: row.cargo,
  regimen: row.regimen || row.regimen_codigo || '',
  regimen_codigo: row.regimen_codigo || '',
  banco: row.banco || '',
  cuenta_bancaria: row.cuenta_bancaria || '',
  promedio6Meses: row.promedio6Meses ?? 0,
  diasTrabajados: row.diasTrabajados ?? 0,
  gratificacionBruta: row.gratificacionBruta ?? 0,
  bonificacion: row.bonificacion ?? 0,
  gratificacionNeta: row.gratificacionNeta ?? 0,
  estado: row.estado || 'calculado',
  tipo_remuneracion: row.tipo_remuneracion,
  base_remuneracion: row.base_remuneracion,
  meses_computables: row.meses_computables ?? null,
  sueldos_mensuales: row.sueldos_mensuales || [],
  mes_desde_ingreso: row.mes_desde_ingreso || null,
  ultima_fecha_ingreso_labores: row.ultima_fecha_ingreso_labores || null,
});

const GratificacionesDetalle = ({ empresaId, selectedPeriodo: initialPeriodo = null, onVolver, onGratificacionActualizada = null }) => {
  const { selectedCompany } = useAuth();
  const [selectedPeriodo, setSelectedPeriodo] = useState(initialPeriodo);
  const [loading, setLoading] = useState(true);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [error, setError] = useState(null);

  const [periodosGratificaciones, setPeriodosGratificaciones] = useState([]);
  const [gratificacionesDetalle, setGratificacionesDetalle] = useState({});
  const [modalVerDetalle, setModalVerDetalle] = useState(null);
  const [ingresosPorMes, setIngresosPorMes] = useState([]);
  const [mesIngresosAbierto, setMesIngresosAbierto] = useState(null);
  /** Meses excluidos del cálculo de la base (nombre del mes, ej. "Julio"). Clic en x para quitar del cálculo. */
  const [mesesExcluidosDelCalculo, setMesesExcluidosDelCalculo] = useState(new Set());
  const [anioFiltro, setAnioFiltro] = useState(new Date().getFullYear());
  const [guardandoConfig, setGuardandoConfig] = useState(false);
  const [generandoExcel, setGenerandoExcel] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [generandoConstancias, setGenerandoConstancias] = useState(false);
  const [generandoConstanciaIndividual, setGenerandoConstanciaIndividual] = useState(false);
  const [firmaEmpresaBase64, setFirmaEmpresaBase64] = useState(null);
  const [empresaSnapshot, setEmpresaSnapshot] = useState(null);
  const [cerrandoGratificacion, setCerrandoGratificacion] = useState(false);
  const [showModalCerrar, setShowModalCerrar] = useState(false);

  const gratificacionId = selectedPeriodo?.gratificacionId ?? initialPeriodo?.gratificacionId ?? null;
  const gratificacionCerrada = (selectedPeriodo?.estado || initialPeriodo?.estado) === 'cerrada';

  useEffect(() => {
    if (!initialPeriodo) return;
    setSelectedPeriodo((prev) => {
      const nextKey = clavePeriodoGratificacion(initialPeriodo);
      const prevKey = clavePeriodoGratificacion(prev);
      // Mantener id `grat-{n}` si ya cargamos el detalle con esa clave (p. ej. tras cerrar).
      if (prev && prevKey && nextKey && prevKey === nextKey) {
        return {
          ...prev,
          ...initialPeriodo,
          id: prevKey,
          gratificacionId: prev.gratificacionId ?? initialPeriodo.gratificacionId ?? initialPeriodo.id,
          estado: initialPeriodo.estado || prev.estado,
        };
      }
      return {
        ...initialPeriodo,
        id: nextKey || initialPeriodo.id,
        gratificacionId: initialPeriodo.gratificacionId ?? initialPeriodo.id,
      };
    });
  }, [initialPeriodo]);

  // Si se abre una gratificación guardada (desde el dashboard), cargar por ID
  useEffect(() => {
    if (!gratificacionId || !empresaId) return;
    setLoading(true);
    setError(null);
    gratificacionService
      .getById(gratificacionId)
      .then((res) => {
        const g = res.data || {};
        const periodoKey = `grat-${g.id}`;
        const periodoObj = {
          id: periodoKey,
          gratificacionId: g.id,
          periodo: g.periodo_nombre || (g.periodo === 'junio' ? 'Junio' : 'Diciembre'),
          periodoKey: g.periodo || (String(g.periodo_nombre || '').toLowerCase().includes('dic') ? 'diciembre' : 'junio'),
          año: g.anio,
          fechaInicio: g.fecha_inicio,
          fechaFin: g.fecha_fin,
          totalEmpleados: g.total_empleados ?? 0,
          totalCalculado: g.total_calculado ?? 0,
          estado: g.estado === 'cerrada' ? 'cerrada' : 'calculado',
          entrara_en_planilla:
            g.entrara_en_planilla === true || g.entrara_en_planilla === false
              ? g.entrara_en_planilla
              : null,
          empresa_snapshot: g.empresa_snapshot || null,
        };
        setEmpresaSnapshot(g.empresa_snapshot || null);
        setPeriodosGratificaciones([periodoObj]);
        setSelectedPeriodo(periodoObj);
        const detalleList = (g.detalle || []).map(mapApiFilaToDetalle);
        setGratificacionesDetalle((prev) => ({ ...prev, [periodoKey]: detalleList }));
      })
      .catch((err) => {
        console.error('Error cargando gratificación:', err);
        setError(err.response?.data?.message || 'Error al cargar gratificación');
        setPeriodosGratificaciones([]);
        setGratificacionesDetalle({});
      })
      .finally(() => setLoading(false));
  }, [gratificacionId, empresaId]);

  // Cargar períodos desde API (solo cuando NO estamos abriendo una gratificación guardada)
  const loadPeriodos = useCallback(async () => {
    if (!empresaId || gratificacionId) {
      if (!gratificacionId) setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await gratificacionService.getPeriodos(empresaId, anioFiltro);
      const list = (res.data || []).map((p) => ({
        id: p.id,
        periodo: p.periodo,
        año: p.año,
        fechaInicio: p.fecha_inicio,
        fechaFin: p.fecha_fin,
        totalEmpleados: 0,
        totalCalculado: 0,
        estado: 'pendiente',
      }));
      setPeriodosGratificaciones(list);
    } catch (err) {
      console.error('Error cargando períodos de gratificación:', err);
      setError(err.response?.data?.message || 'Error al cargar períodos');
      setPeriodosGratificaciones([]);
    } finally {
      setLoading(false);
    }
  }, [empresaId, anioFiltro, gratificacionId]);

  useEffect(() => {
    if (!gratificacionId) loadPeriodos();
  }, [loadPeriodos, gratificacionId]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!empresaId) {
        setFirmaEmpresaBase64(null);
        return;
      }
      const b64 = await obtenerFirmaEmpresaBase64(empresaId);
      if (!cancel) setFirmaEmpresaBase64(b64);
    })();
    return () => {
      cancel = true;
    };
  }, [empresaId]);

  const empresaParaConstancia = useMemo(
    () => ({
      nombre:
        empresaSnapshot?.nombre ??
        selectedPeriodo?.empresa_snapshot?.nombre ??
        selectedCompany?.name ??
        selectedCompany?.nombre_empresa,
      ruc: empresaSnapshot?.ruc ?? selectedPeriodo?.empresa_snapshot?.ruc ?? selectedCompany?.ruc,
      direccion: selectedCompany?.direccion ?? selectedCompany?.address ?? '',
    }),
    [empresaSnapshot, selectedPeriodo, selectedCompany]
  );

  const opcionesConstanciaGrat = useMemo(() => {
    const rep =
      empresaSnapshot?.representante_legal ??
      selectedPeriodo?.empresa_snapshot?.representante_legal ??
      selectedCompany?.representante_legal ??
      '';
    return {
      representanteLegal: String(rep).trim() || 'Representante Legal',
    };
  }, [empresaSnapshot, selectedPeriodo, selectedCompany]);

  const gratificacionParaConstancia = useMemo(() => {
    if (!selectedPeriodo) return null;
    const periodoKey =
      selectedPeriodo.periodoKey ||
      (String(selectedPeriodo.periodo || '').toLowerCase().includes('dic') ? 'diciembre' : 'junio');
    return {
      anio: selectedPeriodo.año,
      periodo: periodoKey,
      periodo_nombre: selectedPeriodo.periodo,
      fecha_inicio: selectedPeriodo.fechaInicio,
      fecha_fin: selectedPeriodo.fechaFin,
    };
  }, [selectedPeriodo]);

  const handleDescargarConstanciaIndividual = async (fila) => {
    if (!fila || !gratificacionParaConstancia) return;
    try {
      setGenerandoConstanciaIndividual(true);
      await descargarConstanciaGratificacionIndividual({
        empresa: empresaParaConstancia,
        gratificacion: gratificacionParaConstancia,
        detalleRow: fila,
        firmaEmpresaBase64,
        opciones: opcionesConstanciaGrat,
      });
    } catch (err) {
      console.error(err);
      alert(err.message || 'No se pudo generar la constancia.');
    } finally {
      setGenerandoConstanciaIndividual(false);
    }
  };

  const handleExportarTodasConstancias = async () => {
    const detalle = getCurrentDetalleData();
    if (!gratificacionParaConstancia || detalle.length === 0) return;
    try {
      setGenerandoConstancias(true);
      const archivos = await exportarTodasConstanciasGratificacion({
        empresa: empresaParaConstancia,
        gratificacion: gratificacionParaConstancia,
        detalle,
        firmaEmpresaBase64,
        opciones: opcionesConstanciaGrat,
      });
      const msg =
        archivos.length === 1
          ? `Constancia(s) generada(s): ${archivos[0]}`
          : `Se generaron ${archivos.length} archivos PDF:\n${archivos.join('\n')}`;
      alert(msg);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al generar constancias.');
    } finally {
      setGenerandoConstancias(false);
    }
  };

  // Al hacer clic en "Ver Detalle" de un período, cargar cálculo desde API
  const handleViewDetalle = async (periodo) => {
    setSelectedPeriodo(periodo);
    const periodoKey = periodo.periodo === 'Junio' ? 'junio' : 'diciembre';
    const anio = periodo.año;
    if (!empresaId) return;
    const yaCargado = gratificacionesDetalle[periodo.id] && gratificacionesDetalle[periodo.id].length > 0;
    if (yaCargado) return;
    setLoadingDetalle(true);
    setError(null);
    try {
      const res = await gratificacionService.calcular(empresaId, periodoKey, anio);
      const lista = (res.data || []).map(mapApiFilaToDetalle);
      setGratificacionesDetalle((prev) => ({ ...prev, [periodo.id]: lista }));
      setPeriodosGratificaciones((prev) =>
        prev.map((p) =>
          p.id === periodo.id
            ? {
                ...p,
                totalEmpleados: res.total_empleados ?? lista.length,
                totalCalculado: res.total_gratificacion ?? 0,
                estado: 'calculado',
              }
            : p
        )
      );
    } catch (err) {
      console.error('Error calculando gratificación:', err);
      setError(err.response?.data?.message || 'Error al calcular gratificación');
      setGratificacionesDetalle((prev) => ({ ...prev, [periodo.id]: [] }));
    } finally {
      setLoadingDetalle(false);
    }
  };

  const formatCurrency = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return 'S/ 0.00';
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(value);
  };

  // Fechas YYYY-MM-DD / ISO sin desfase por zona horaria (America/Lima)
  const formatDateToDDMMYYYY = (dateString) => formatFechaSoloDiaDdMmYyyy(dateString, 'N/A');

  const esRegimenMypePequenaEmpresa = (regimenCodigo, regimenNombre) => {
    const codigo = (regimenCodigo || '').toUpperCase();
    const nombre = (regimenNombre || '').toUpperCase();
    return codigo === 'MYPP' || codigo === 'MYPE' || nombre.includes('PEQUEÑA EMPRESA') || nombre.includes('MYPE');
  };

  const getEstadoBadge = (estado) => {
    const estadoConfig = {
      proceso: { class: 'bg-blue-100 text-blue-800', text: 'En Proceso', icon: '⚙️' },
      cerrada: { class: 'bg-green-100 text-green-800', text: 'Cerrada', icon: '✅' },
      anulada: { class: 'bg-red-100 text-red-800', text: 'Anulada', icon: '❌' },
      calculado: { class: 'bg-yellow-100 text-yellow-800', text: 'Calculado', icon: '🧮' },
      pendiente: { class: 'bg-orange-100 text-orange-800', text: 'Pendiente', icon: '⏳' },
      pagado: { class: 'bg-green-100 text-green-800', text: 'Pagado', icon: '💰' },
      en_proceso: { class: 'bg-blue-100 text-blue-800', text: 'En Proceso', icon: '⚙️' }
    };
    return estadoConfig[estado] || estadoConfig.pendiente;
  };

  const handleBackToMain = () => {
    if (initialPeriodo && onVolver) {
      // Si viene de un periodo inicial (desde Calculos), usar la función onVolver
      onVolver();
    } else {
      // Si es navegación interna, volver a la lista de periodos
      setSelectedPeriodo(null);
    }
  };

  const getCurrentDetalleData = () => {
    if (!selectedPeriodo) return [];
    const key = clavePeriodoGratificacion(selectedPeriodo);
    if (key && Array.isArray(gratificacionesDetalle[key])) {
      return gratificacionesDetalle[key];
    }
    if (selectedPeriodo.id != null && Array.isArray(gratificacionesDetalle[selectedPeriodo.id])) {
      return gratificacionesDetalle[selectedPeriodo.id];
    }
    const gid = selectedPeriodo.gratificacionId;
    if (gid != null && Array.isArray(gratificacionesDetalle[gid])) {
      return gratificacionesDetalle[gid];
    }
    return [];
  };

  // Recalcular promedio, base y montos para reportes según meses incluidos (excluido_del_calculo)
  const calculeValoresReporte = (g) => {
    const incluidos = (g.sueldos_mensuales || []).filter((s) => !s.excluido_del_calculo);
    const sueldosParaCalc = incluidos.map((s) => ({ mes: s.mes, monto: Number(s.monto) || 0 }));
    const tipoRem = getTipoRemuneracion(sueldosParaCalc);
    const mesesPeriodo = 6;
    const factorMeses = sueldosParaCalc.length > 0 ? sueldosParaCalc.length / mesesPeriodo : 0;
    let gratBruta = Math.round(tipoRem.baseRemuneracion * factorMeses * 100) / 100;
    const esPequenaEmpresa = esRegimenMypePequenaEmpresa(g.regimen_codigo, g.regimen);
    if (esPequenaEmpresa) gratBruta = Math.round(gratBruta * 0.5 * 100) / 100;
    const bonif = Math.round(gratBruta * 0.09 * 100) / 100;
    const totalPagar = Math.round((gratBruta + bonif) * 100) / 100;
    const promedio = tipoRem.esFija ? tipoRem.baseRemuneracion : (tipoRem.promedio ?? tipoRem.baseRemuneracion);
    return {
      promedio: Math.round((promedio || 0) * 100) / 100,
      base: tipoRem.baseRemuneracion,
      tipoRemuneracion: tipoRem.esFija ? 'fija' : 'variable',
      gratBruta,
      bonif,
      totalPagar,
    };
  };

  const handleAbrirModalCerrar = () => {
    if (!gratificacionId || gratificacionCerrada || cerrandoGratificacion) return;
    setShowModalCerrar(true);
  };

  const handleConfirmarCerrarGratificacion = async (entraraPlanilla) => {
    if (!gratificacionId || gratificacionCerrada || cerrandoGratificacion) return;
    setCerrandoGratificacion(true);
    setError(null);
    try {
      const res = await gratificacionService.cerrar(gratificacionId, entraraPlanilla);
      const g = res.data || {};
      setShowModalCerrar(false);
      setSelectedPeriodo((prev) =>
        prev
          ? {
              ...prev,
              estado: 'cerrada',
              entrara_en_planilla: entraraPlanilla === true,
              totalCalculado: g.total_calculado ?? prev.totalCalculado,
              totalEmpleados: g.total_empleados ?? prev.totalEmpleados,
            }
          : prev
      );
      if (typeof onGratificacionActualizada === 'function') {
        await onGratificacionActualizada();
      }
      alert(res.message || 'Gratificación cerrada exitosamente');
    } catch (err) {
      console.error('Error cerrando gratificación:', err);
      setError(err.response?.data?.message || 'Error al cerrar la gratificación');
      alert(err.response?.data?.message || 'Error al cerrar la gratificación');
    } finally {
      setCerrandoGratificacion(false);
    }
  };

  // Exportar gratificaciones a Excel (ExcelJS) - misma calidad que PlanillaDetalle
  const exportarGratificacionesExcel = async () => {
    const detalle = getCurrentDetalleData();
    if (detalle.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }
    try {
      setGenerandoExcel(true);
      const periodoNombre = selectedPeriodo?.periodo || 'Gratificación';
      const año = selectedPeriodo?.año || new Date().getFullYear();
      const esJunio = (periodoNombre || '').toLowerCase() === 'junio';
      const mesKeys = esJunio ? ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio'] : ['Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      const datosExcel = detalle.map((g) => {
        const porMes = {};
        (g.sueldos_mensuales || []).forEach((s) => {
          porMes[s.mes] = s.excluido_del_calculo ? 0 : (Number(s.monto) || 0);
        });
        const calc = calculeValoresReporte(g);
        const fila = {
          Empleado: g.empleado || '',
          Cargo: g.cargo || '',
          Régimen: g.regimen || '',
          ...Object.fromEntries(mesKeys.map((m) => [m, porMes[m] ?? 0])),
          'Tipo Remun.': calc.tipoRemuneracion || g.tipo_remuneracion || 'variable',
          'Días Trabajados': g.diasTrabajados ?? 0,
          'Promedio 6M': calc.promedio,
          'Base Remuneración': calc.base,
          'Gratificación Bruta': calc.gratBruta,
          'Bonificación (9%)': calc.bonif,
          'Total a Pagar': calc.totalPagar,
        };
        return fila;
      });
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Sistema Planillas';
      workbook.created = new Date();
      const headers = datosExcel.length ? Object.keys(datosExcel[0]) : [];
      const ws = workbook.addWorksheet('Gratificaciones', {
        views: [{ state: 'frozen', ySplit: 4, activeCell: 'A5' }],
        properties: { defaultRowHeight: 20 },
      });
      const rowTitulo = ws.getRow(1);
      rowTitulo.getCell(1).value = `GRATIFICACIÓN ${periodoNombre} ${año}`;
      ws.mergeCells(1, 1, 1, Math.max(headers.length, 1));
      rowTitulo.getCell(1).font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      rowTitulo.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
      rowTitulo.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      rowTitulo.height = 28;
      const rowSub = ws.getRow(2);
      const fechaIni = selectedPeriodo?.fechaInicio ? formatDateToDDMMYYYY(selectedPeriodo.fechaInicio) : 'N/A';
      const fechaFin = selectedPeriodo?.fechaFin ? formatDateToDDMMYYYY(selectedPeriodo.fechaFin) : 'N/A';
      rowSub.getCell(1).value = `Período: ${fechaIni} - ${fechaFin}  |  Exportado: ${new Date().toLocaleDateString('es-PE')}`;
      ws.mergeCells(2, 1, 2, Math.max(headers.length, 1));
      rowSub.getCell(1).font = { size: 10, color: { argb: 'FF6B7280' } };
      rowSub.getCell(1).alignment = { horizontal: 'center' };
      rowSub.height = 20;
      ws.getRow(3).height = 8;
      const rowHeader = ws.getRow(4);
      headers.forEach((h, i) => {
        const cell = rowHeader.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D9488' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      rowHeader.height = 22;
      const numericKeys = new Set(['Días Trabajados', 'Promedio 6M', 'Base Remuneración', 'Gratificación Bruta', 'Bonificación (9%)', 'Total a Pagar', ...mesKeys]);
      datosExcel.forEach((fila, idx) => {
        const row = ws.getRow(5 + idx);
        headers.forEach((key, i) => {
          const cell = row.getCell(i + 1);
          const val = fila[key];
          cell.value = val;
          if (numericKeys.has(key) && typeof val === 'number') {
            cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: 'right' };
          }
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0FDF4' } };
        });
      });
      const totalesRowIdx = 5 + datosExcel.length;
      const rowTotales = ws.getRow(totalesRowIdx);
      const indiceGrat = headers.indexOf('Gratificación Bruta');
      headers.forEach((key, i) => {
        const cell = rowTotales.getCell(i + 1);
        if (i === 0) {
          cell.value = 'TOTALES';
          cell.font = { bold: true };
        } else if (numericKeys.has(key)) {
          const total = datosExcel.reduce((s, f) => s + (Number(f[key]) || 0), 0);
          cell.value = total;
          cell.numFmt = '#,##0.00';
          cell.font = { bold: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
        }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      ws.columns.forEach((col, i) => { col.width = Math.min(Math.max(headers[i]?.length || 10, 12), 28); });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gratificacion_${periodoNombre}_${año}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      alert('Archivo Excel generado exitosamente');
    } catch (err) {
      console.error('Error exportando Excel:', err);
      alert('Error al generar el Excel: ' + err.message);
    } finally {
      setGenerandoExcel(false);
    }
  };

  // Exportar gratificaciones a PDF - misma calidad que PlanillaDetalle (html2canvas)
  const exportarGratificacionesPDF = async () => {
    const detalle = getCurrentDetalleData();
    if (detalle.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }
    try {
      setGenerandoPDF(true);
      const periodoNombre = selectedPeriodo?.periodo || 'Gratificación';
      const año = selectedPeriodo?.año || new Date().getFullYear();
      const esJunio = (periodoNombre || '').toLowerCase() === 'junio';
      const mesKeys = esJunio ? ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio'] : ['Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      let totalGrat = 0;
      let totalBonif = 0;
      let totalNeto = 0;
      let filasHtml = '';
      detalle.forEach((g, idx) => {
        const calc = calculeValoresReporte(g);
        totalGrat += calc.gratBruta;
        totalBonif += calc.bonif;
        totalNeto += calc.totalPagar;
        const porMes = {};
        (g.sueldos_mensuales || []).forEach((s) => {
          porMes[s.mes] = s.excluido_del_calculo ? 0 : (Number(s.monto) || 0);
        });
        const celdasMes = mesKeys.map((m) => `<td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;color:#065f46;">${formatCurrency(porMes[m] ?? 0)}</td>`).join('');
        filasHtml += `
          <tr style="background-color:${idx % 2 === 0 ? '#fff' : '#f9fafb'};">
            <td style="border:1px solid #d1d5db;padding:4px 6px;font-size:8px;color:#111827;min-width:140px;">${g.empleado || ''}<br><span style="color:#6b7280;font-size:7px;">${g.cargo || ''} • ${g.regimen || ''}</span></td>
            ${celdasMes}
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;color:#065f46;">${formatCurrency(calc.promedio)}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:center;font-size:8px;">${calc.tipoRemuneracion}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;font-weight:bold;color:#047857;">${formatCurrency(calc.gratBruta)}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;">${formatCurrency(calc.bonif)}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;font-weight:bold;color:#0d9488;">${formatCurrency(calc.totalPagar)}</td>
          </tr>
        `;
      });
      let headersMes = mesKeys.map((m) => `<th style="border:1px solid #d1d5db;padding:6px 4px;text-align:center;font-weight:bold;color:#065f46;background:#d1fae5;font-size:8px;">${m}</th>`).join('');
      const contenedor = document.createElement('div');
      contenedor.style.cssText = 'position:absolute;left:-9999px;top:0;width:1200px;background:#fff;padding:15px;font-family:Arial,sans-serif;';
      contenedor.innerHTML = `
        <div style="text-align:center;margin-bottom:15px;">
          <h1 style="color:#1f2937;margin-bottom:6px;font-size:16px;font-weight:bold;">Detalle de Gratificaciones - ${periodoNombre} ${año}</h1>
          <p style="color:#6b7280;font-size:10px;">Período: ${selectedPeriodo?.fechaInicio ? formatDateToDDMMYYYY(selectedPeriodo.fechaInicio) : 'N/A'} - ${selectedPeriodo?.fechaFin ? formatDateToDDMMYYYY(selectedPeriodo.fechaFin) : 'N/A'}</p>
          <p style="color:#6b7280;font-size:10px;">Generado: ${new Date().toLocaleDateString('es-PE')}</p>
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:#374151;background:#f9fafb;font-size:8px;min-width:140px;">Empleado</th>
              ${headersMes}
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:#047857;background:#d1fae5;font-size:8px;">Prom. 6M</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:#374151;background:#f9fafb;font-size:8px;">Tipo</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:white;background:#047857;font-size:8px;">Gratificación</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:white;background:#0d9488;font-size:8px;">Bonif. 9%</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:white;background:#0d9488;font-size:8px;">Total</th>
            </tr>
          </thead>
          <tbody>${filasHtml}</tbody>
          <tfoot>
            <tr style="background:#f3f4f6;font-weight:bold;">
              <td style="border:1px solid #d1d5db;padding:4px 6px;font-size:8px;">TOTALES</td>
              ${mesKeys.map(() => '<td style="border:1px solid #d1d5db;padding:4px;"></td>').join('')}
              <td style="border:1px solid #d1d5db;padding:4px;"></td>
              <td style="border:1px solid #d1d5db;padding:4px;"></td>
              <td style="border:1px solid #d1d5db;padding:4px;text-align:right;color:#047857;">${formatCurrency(totalGrat)}</td>
              <td style="border:1px solid #d1d5db;padding:4px;text-align:right;color:#0d9488;">${formatCurrency(totalBonif)}</td>
              <td style="border:1px solid #d1d5db;padding:4px;text-align:right;color:#0d9488;">${formatCurrency(totalNeto)}</td>
            </tr>
          </tfoot>
        </table>
        <p style="margin-top:10px;text-align:center;color:#6b7280;font-size:8px;">Total empleados: ${detalle.length}</p>
      `;
      document.body.appendChild(contenedor);
      const canvas = await html2canvas(contenedor, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: contenedor.scrollWidth,
        height: contenedor.scrollHeight,
        logging: false,
      });
      document.body.removeChild(contenedor);
      const imgData = canvas.toDataURL('image/png', 1.0);
      const canvasW = (canvas.width * 25.4) / 96;
      const canvasH = (canvas.height * 25.4) / 96;
      const pdfW = Math.max(canvasW, 210);
      const pdfH = Math.max(canvasH, 297);
      const pdf = new jsPDF({
        orientation: pdfW > pdfH ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfW, pdfH],
      });
      const scale = Math.min(pdfW / canvasW, pdfH / canvasH, 1);
      pdf.addImage(imgData, 'PNG', (pdfW - canvasW * scale) / 2, (pdfH - canvasH * scale) / 2, canvasW * scale, canvasH * scale);
      pdf.save(`Gratificacion_${periodoNombre}_${año}_${new Date().toISOString().split('T')[0]}.pdf`);
      alert('PDF generado exitosamente');
    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('Error al generar el PDF: ' + err.message);
    } finally {
      setGenerandoPDF(false);
    }
  };

  // Al abrir el modal de detalle: usar sueldos_mensuales de la API si existen, si no mock
  useEffect(() => {
    if (!modalVerDetalle) {
      setIngresosPorMes([]);
      setMesIngresosAbierto(null);
      return;
    }
    const sueldosApi = modalVerDetalle.sueldos_mensuales;
    const mesDesdeIngreso = modalVerDetalle.mes_desde_ingreso || null;
    const excluidosInicial = new Set();
    let filas;
    if (sueldosApi && Array.isArray(sueldosApi) && sueldosApi.length > 0) {
      let todas = sueldosApi.map((s) => {
        if (s.excluido_del_calculo && s.mes) excluidosInicial.add(s.mes);
        const requiereManual = !!s.requiere_monto_manual || s.conceptos_origen === 'manual';
        return {
          mes: s.mes,
          mes_key: s.mes_key,
          monto: Number(s.monto) ?? (s.monto_editado != null ? Number(s.monto_editado) : 0),
          montoEditado: s.monto_editado != null ? Number(s.monto_editado) : undefined,
          pendienteCierre: !!s.pendiente_cierre,
          requiereMontoManual: requiereManual,
          leyenda: s.pendiente_cierre
            ? 'Promedio Jul-Nov (planilla dic. no cerrada)'
            : requiereManual
              ? 'Sin planilla cerrada: indique el monto ganado'
              : undefined,
          conceptos: s.conceptos,
          conceptosOrigen: s.conceptos_origen,
        };
      });
      filas = mesDesdeIngreso
        ? todas.filter((s) => s.mes_key && s.mes_key >= mesDesdeIngreso)
        : todas;
      setMesesExcluidosDelCalculo(excluidosInicial);
    } else {
      filas = getUltimosSueldosMock(modalVerDetalle.promedio6Meses);
      setMesesExcluidosDelCalculo(new Set());
    }
    const conConceptos = filas.map((row) => {
      const conceptosDeApi = row.conceptos && Array.isArray(row.conceptos) && row.conceptos.length > 0;
      const conceptos = conceptosDeApi
        ? row.conceptos.map((c, i) => ({
            id: `${row.mes}-${c.codigo_plame || i}-${i}`,
            nombre: c.nombre || c.descripcion_concepto || `Concepto ${i + 1}`,
            monto: Number(c.monto) || 0,
            incluir: c.incluir !== false,
          }))
        : getConceptosIngresoMock(row.mes, row.monto);
      return {
        mes: row.mes,
        mes_key: row.mes_key,
        monto: row.monto,
        montoEditado: row.montoEditado,
        pendienteCierre: row.pendienteCierre || false,
        requiereMontoManual: !!row.requiereMontoManual,
        leyenda: row.leyenda,
        conceptos,
        conceptosOrigen: row.conceptosOrigen,
        esConceptosPlame: conceptosDeApi && row.conceptosOrigen === 'planilla',
      };
    });
    setIngresosPorMes(conConceptos);
    setMesIngresosAbierto(null);
    if (!(sueldosApi && Array.isArray(sueldosApi) && sueldosApi.length > 0)) {
      setMesesExcluidosDelCalculo(new Set());
    }
  }, [modalVerDetalle]);

  // Conceptos que no se consideran para el cálculo (Gratificación, Bonificación Extraordinaria, CTS).
  const esConceptoExcluidoParaGratificacion = (nombre) => {
    if (!nombre || typeof nombre !== 'string') return false;
    const n = nombre.toUpperCase();
    if ((n.includes('GRATIFICACION') && (n.includes('FIESTAS PATRIAS') || n.includes('NAVIDAD')))) return true;
    if (n.includes('BONIFICACIÓN EXTRAORDINARIA') || n.includes('BONIFICACION EXTRAORDINARIA')) return true;
    if (n.includes('COMPENSACIÓN POR TIEMPO DE SERVICIOS') || n.includes('COMPENSACION POR TIEMPO DE SERVICIOS')) return true;
    return false;
  };

  // Total del mes = monto editado si existe, si no suma de conceptos incluidos (excluyendo gratificación y bonificación extraordinaria).
  const getTotalMes = (itemMes) => {
    if (itemMes?.montoEditado !== undefined && itemMes.montoEditado !== null && itemMes.montoEditado !== '') {
      return Number(itemMes.montoEditado) || 0;
    }
    if (!itemMes || !itemMes.conceptos) return 0;
    return itemMes.conceptos
      .filter((c) => c.incluir && !esConceptoExcluidoParaGratificacion(c.nombre))
      .reduce((a, c) => a + c.monto, 0);
  };

  const updateMontoMes = (mesNombre, value) => {
    const num = value === '' ? undefined : Number(value);
    setIngresosPorMes((prev) =>
      prev.map((item) => (item.mes !== mesNombre ? item : { ...item, montoEditado: num }))
    );
  };

  const toggleMesExcluidoDelCalculo = (mesNombre) => {
    setMesesExcluidosDelCalculo((prev) => {
      const next = new Set(prev);
      if (next.has(mesNombre)) next.delete(mesNombre);
      else next.add(mesNombre);
      return next;
    });
  };

  const nombresMesAMesKey = { Enero: '01', Febrero: '02', Marzo: '03', Abril: '04', Mayo: '05', Junio: '06', Julio: '07', Agosto: '08', Septiembre: '09', Octubre: '10', Noviembre: '11', Diciembre: '12' };

  /** Meses que cuentan para base/factor: no excluidos y con monto > 0 (los manuales en 0 esperan captura). */
  const getSueldosIncluidosParaCalculo = () =>
    ingresosPorMes.filter((item) => {
      if (mesesExcluidosDelCalculo.has(item.mes)) return false;
      return getTotalMes(item) > 0;
    });

  const handleGuardarConfig = async () => {
    if (!gratificacionId || !modalVerDetalle || gratificacionCerrada) return;
    const año = selectedPeriodo?.año || new Date().getFullYear();
    const sueldosIncluidos = getSueldosIncluidosParaCalculo();
    const sueldosParaCalculo = sueldosIncluidos.map((item) => ({ mes: item.mes, monto: getTotalMes(item) }));
    const tipoRem = getTipoRemuneracion(sueldosParaCalculo);
    const mesesPeriodo = 6;
    const factorMeses = sueldosIncluidos.length > 0 ? sueldosIncluidos.length / mesesPeriodo : 0;
    let gratBruta = Math.round(tipoRem.baseRemuneracion * factorMeses * 100) / 100;
    const esPequenaEmpresa = esRegimenMypePequenaEmpresa(modalVerDetalle.regimen_codigo, modalVerDetalle.regimen);
    if (esPequenaEmpresa) gratBruta = Math.round(gratBruta * 0.5 * 100) / 100;
    const bonif = Math.round(gratBruta * 0.09 * 100) / 100;
    const totalPagar = Math.round((gratBruta + bonif) * 100) / 100;
    const sueldos_mensuales = ingresosPorMes.map((item) => {
      const total = getTotalMes(item);
      const mesKey = item.mes_key || (año + '-' + (nombresMesAMesKey[item.mes] || '01'));
      const conceptos = (item.conceptos || []).map((c) => ({
        codigo_plame: c.codigo_plame,
        nombre: c.nombre,
        monto: Number(c.monto) || 0,
        incluir: c.incluir !== false,
      }));
      return {
        mes: item.mes,
        mes_key: mesKey,
        monto: total,
        monto_editado: item.montoEditado != null ? item.montoEditado : undefined,
        pendiente_cierre: !!item.pendienteCierre,
        requiere_monto_manual: !!item.requiereMontoManual && getTotalMes(item) <= 0,
        excluido_del_calculo: mesesExcluidosDelCalculo.has(item.mes),
        conceptos,
        conceptos_origen: item.conceptosOrigen || (item.requiereMontoManual ? 'manual' : 'sin_desglose'),
      };
    });
    setGuardandoConfig(true);
    try {
      const res = await gratificacionService.updateDetalle(gratificacionId, modalVerDetalle.id, {
        sueldos_mensuales,
        gratificacion_bruta: gratBruta,
        bonificacion: bonif,
        total: totalPagar,
      });
      const g = res.data || {};
      const periodoKey = selectedPeriodo?.id || `grat-${gratificacionId}`;
      const detalleList = (g.detalle || []).map(mapApiFilaToDetalle);
      setGratificacionesDetalle((prev) => ({ ...prev, [periodoKey]: detalleList }));
      const filaActualizada = detalleList.find((d) => d.id === modalVerDetalle.id);
      if (filaActualizada) {
        setModalVerDetalle((prev) => ({ ...prev, ...filaActualizada, gratificacionBruta: gratBruta, bonificacion: bonif, gratificacionNeta: totalPagar }));
      }
      if (g.total_calculado != null && selectedPeriodo) {
        setSelectedPeriodo((p) => (p ? { ...p, totalCalculado: g.total_calculado } : p));
      }
    } catch (err) {
      console.error('Error guardando configuración:', err);
      setError(err.response?.data?.message || 'Error al guardar la configuración');
    } finally {
      setGuardandoConfig(false);
    }
  };

  const toggleConceptoIncluir = (mesNombre, conceptoId) => {
    setIngresosPorMes((prev) =>
      prev.map((item) => {
        if (item.mes !== mesNombre) return item;
        return {
          ...item,
          conceptos: item.conceptos.map((c) =>
            c.id === conceptoId ? { ...c, incluir: !c.incluir } : c
          )
        };
      })
    );
  };

  // Período de sueldos según gratificación: Julio → Enero a Junio; Noviembre → Julio a Diciembre
  const getPeriodoSueldos = () => {
    if (!selectedPeriodo) return { texto: '', meses: [] };
    const esJulio = (selectedPeriodo.periodo || '').toLowerCase() === 'junio'; // Gratificación de julio se paga en julio, período de sueldos es ene-jun
    const año = selectedPeriodo.año || new Date().getFullYear();
    if (esJulio) {
      return {
        texto: `Enero a Junio ${año}`,
        meses: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio'],
        año
      };
    }
    return {
      texto: `Julio a Diciembre ${año}`,
      meses: ['Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
      año
    };
  };

  // Gratificación de noviembre: la planilla de diciembre aún no se cierra, por eso Diciembre se rellena con el promedio Jul-Nov
  const esPeriodoDiciembre = selectedPeriodo && (selectedPeriodo.periodo || '').toLowerCase() !== 'junio';

  // Mock: conceptos de ingreso por mes (desglose). Suma = montoTotal; todos incluir: true por defecto
  const getConceptosIngresoMock = (mes, montoTotal) => {
    const total = Number(montoTotal) || 0;
    const partes = [0.65, 0.15, 0.10, 0.05, 0.05]; // sueldo, HE, bonificación, asig. familiar, otros
    const nombres = ['Sueldo básico', 'Horas extras', 'Bonificación', 'Asignación familiar', 'Otros ingresos'];
    return nombres.map((nombre, i) => {
      const monto = Math.round((total * partes[i]) * 100) / 100;
      return { id: `${mes}-${i}`, nombre, monto, incluir: true };
    }).map((c, i, arr) => {
      if (i === arr.length - 1) {
        const suma = arr.slice(0, -1).reduce((a, x) => a + x.monto, 0);
        return { ...c, monto: Math.round((total - suma) * 100) / 100 };
      }
      return c;
    });
  };

  // Mock: generar últimos sueldos (6 meses). En período Julio-Dic, si no hay remuneración de diciembre, Diciembre = promedio Jul-Nov
  const getUltimosSueldosMock = (promedio6Meses) => {
    const periodoSueldos = getPeriodoSueldos();
    const base = Number(promedio6Meses) || 0;
    const variacion = base * 0.02;

    if (esPeriodoDiciembre) {
      // Solo tenemos Jul a Nov (5 meses). Diciembre se rellena con el promedio de esos 5 (se calcula antes de cerrar planilla)
      const montos5 = [
        base - variacion,
        base + variacion * 0.5,
        base,
        base - variacion * 0.5,
        base + variacion
      ].map((v) => Math.round(v * 100) / 100);
      const promedioJulNov = montos5.reduce((a, b) => a + b, 0) / 5;
      const promedioJulNovRedondeado = Math.round(promedioJulNov * 100) / 100;
      const filas = periodoSueldos.meses.slice(0, 5).map((mes, i) => ({ mes, monto: montos5[i], pendienteCierre: false }));
      filas.push({
        mes: 'Diciembre',
        monto: promedioJulNovRedondeado,
        pendienteCierre: true,
        leyenda: 'Promedio Jul-Nov (planilla dic. no cerrada)'
      });
      return filas;
    }

    return [
      base - variacion,
      base + variacion * 0.5,
      base,
      base - variacion * 0.5,
      base + variacion,
      base
    ].map((v, i) => ({ mes: periodoSueldos.meses[i] || `Mes ${i + 1}`, monto: Math.round(v * 100) / 100, pendienteCierre: false }));
  };

  // Determinar si la remuneración es fija o variable según los meses disponibles (desde ingreso).
  // 1 mes → automáticamente fija. ≥3 meses y últimos 3 iguales → fija. Resto → variable.
  const getTipoRemuneracion = (sueldosMensuales) => {
    if (!sueldosMensuales || sueldosMensuales.length === 0) {
      return { esFija: false, baseRemuneracion: 0, texto: 'Variable', detalle: 'No hay datos de sueldos.' };
    }
    const montos = sueldosMensuales.map((s) => Number(s.monto) || 0);
    const cantidadMeses = montos.length;
    const promedio = montos.reduce((a, b) => a + b, 0) / cantidadMeses;
    const ultimoSueldo = montos[montos.length - 1] || 0;

    let esFija = false;
    if (cantidadMeses === 1 && ultimoSueldo > 0) {
      esFija = true; // Un solo mes → automáticamente fijo
    } else if (cantidadMeses >= 3) {
      const ultimosTres = montos.slice(-3);
      esFija =
        ultimosTres[0] === ultimosTres[1] &&
        ultimosTres[1] === ultimosTres[2] &&
        ultimosTres.every((m) => m > 0);
    }

    const baseRemuneracion = esFija ? ultimoSueldo : Math.round(promedio * 100) / 100;
    const textoMeses = cantidadMeses === 1 ? '1 mes' : `${cantidadMeses} meses`;

    return {
      esFija,
      baseRemuneracion: baseRemuneracion,
      texto: esFija ? 'Remuneración fija' : 'Remuneración variable',
      detalle: esFija
        ? cantidadMeses === 1
          ? 'Un solo mes disponible. Se considera remuneración fija. Base = sueldo del mes.'
          : `Más de 3 meses con la misma remuneración (${textoMeses} considerados). Base = último sueldo.`
        : cantidadMeses < 6
          ? `Menos de 6 meses: se considera el promedio de los ${textoMeses} disponibles. Base = promedio.`
          : selectedPeriodo?.periodo === 'Junio'
            ? 'Base = promedio de Enero a Junio.'
            : 'Base = promedio de Julio a Diciembre.',
      ultimoSueldo,
      promedio
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // Si viene un periodo inicial, mostramos directamente el detalle
  const showPeriodosList = !selectedPeriodo && !initialPeriodo;
  const showDetalleEmpleados = selectedPeriodo || initialPeriodo;

  return (
    <div className="space-y-3 lg:space-y-4">
      {!empresaId && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
          Seleccione una empresa para ver los períodos de gratificación.
        </div>
      )}
      {showPeriodosList && (
        <>
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 p-4 lg:p-6">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">🎁 Períodos de Gratificaciones</h1>
              <p className="text-gray-600">Gestión de períodos de gratificaciones</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600">Año:</label>
              <select
                value={anioFiltro}
                onChange={(e) => setAnioFiltro(Number(e.target.value))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200" onClick={loadPeriodos}>
                Actualizar
              </button>
            </div>
          </div>
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}
          {/* Lista de Períodos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {periodosGratificaciones.length === 0 && !loading ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay períodos</h3>
                <p className="mt-1 text-sm text-gray-500">Comienza creando un nuevo período de gratificaciones.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Período
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fechas
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Empleados
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {periodosGratificaciones.map((periodo) => {
                        const estadoConfig = getEstadoBadge(periodo.estado);
                        return (
                          <tr key={periodo.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                                  <span className="text-white font-bold text-sm">
                                    {periodo.periodo.charAt(0)}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{periodo.periodo} {periodo.año}</div>
                                  <div className="text-xs text-gray-500">Gratificación semestral</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>{formatDateToDDMMYYYY(periodo.fechaInicio)}</div>
                              <div className="text-xs text-gray-500">{formatDateToDDMMYYYY(periodo.fechaFin)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {periodo.totalEmpleados} empleados
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                              {formatCurrency(periodo.totalCalculado)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                                <span className="mr-1">{estadoConfig.icon}</span>
                                {estadoConfig.text}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-2">
                                <button 
                                  onClick={() => handleViewDetalle(periodo)}
                                  className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                  title="Ver Detalle"
                                >
                                  Ver Detalle
                                </button>
                                <button className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                                  Editar
                                </button>
                                <button className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                                  Eliminar
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden divide-y divide-gray-200">
                  {periodosGratificaciones.map((periodo) => {
                    const estadoConfig = getEstadoBadge(periodo.estado);
                    return (
                      <div key={periodo.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {periodo.periodo.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{periodo.periodo} {periodo.año}</div>
                              <div className="text-xs text-gray-500">Gratificación semestral</div>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                            <span className="mr-1">{estadoConfig.icon}</span>
                            {estadoConfig.text}
                          </span>
                        </div>
                        
                        <div className="mt-3 text-sm text-gray-600">
                          <div>Período: {formatDateToDDMMYYYY(periodo.fechaInicio)} - {formatDateToDDMMYYYY(periodo.fechaFin)}</div>
                          <div>Empleados: {periodo.totalEmpleados} • Total: {formatCurrency(periodo.totalCalculado)}</div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            onClick={() => handleViewDetalle(periodo)}
                            className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                          >
                            Ver Detalle
                          </button>
                          <button className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                            Editar
                          </button>
                          <button className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                            Eliminar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      )}
      
      {showDetalleEmpleados && (
        <>
          {/* Header del Detalle */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 p-4 lg:p-6">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleBackToMain}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Volver
              </button>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  🎁 Detalle de Gratificaciones - {selectedPeriodo?.periodo} {selectedPeriodo?.año}
                </h1>
                <p className="text-gray-600">
                  Período: {formatDateToDDMMYYYY(selectedPeriodo?.fechaInicio)} al {formatDateToDDMMYYYY(selectedPeriodo?.fechaFin)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {gratificacionId && !gratificacionCerrada && (
                <button
                  type="button"
                  onClick={handleAbrirModalCerrar}
                  disabled={cerrandoGratificacion || getCurrentDetalleData().length === 0}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Cerrar gratificación"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{cerrandoGratificacion ? 'Cerrando...' : 'Cerrar'}</span>
                </button>
              )}
              {gratificacionCerrada && (
                <span className="inline-flex flex-col items-start px-3 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                  <span>Cerrada</span>
                  {typeof selectedPeriodo?.entrara_en_planilla === 'boolean' && (
                    <span className="text-[11px] font-normal mt-0.5">
                      {selectedPeriodo.entrara_en_planilla ? 'Incluir en planilla: Sí' : 'Incluir en planilla: No'}
                    </span>
                  )}
                </span>
              )}
              <button
                onClick={exportarGratificacionesExcel}
                disabled={generandoExcel || getCurrentDetalleData().length === 0}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Exportar a Excel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{generandoExcel ? 'Generando...' : 'Excel'}</span>
              </button>
              <button
                type="button"
                onClick={handleExportarTodasConstancias}
                disabled={generandoConstancias || getCurrentDetalleData().length === 0}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Constancias de pago de gratificación (con firma de empresa si está configurada)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>{generandoConstancias ? 'Generando…' : 'Constancias PDF'}</span>
              </button>
              <button
                onClick={exportarGratificacionesPDF}
                disabled={generandoPDF || getCurrentDetalleData().length === 0}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium hover:from-red-600 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Exportar a PDF"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>{generandoPDF ? 'Generando...' : 'PDF'}</span>
              </button>
            </div>
          </div>
          {/* Lista de Empleados */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {error && (
              <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm rounded-t-xl">
                {error}
              </div>
            )}
            {(loadingDetalle || (loading && gratificacionId)) ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500" />
                <span className="ml-3 text-gray-600">{gratificacionId ? 'Cargando detalle...' : 'Calculando gratificaciones...'}</span>
              </div>
            ) : getCurrentDetalleData().length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay empleados</h3>
                <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                  No hay empleados calculados. Para <strong>Junio</strong> se necesitan planillas cerradas de ene–jun;
                  para <strong>Diciembre</strong>, de jul–dic. También revisa régimen (General, Pequeña o Minero General/Pequeña).
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Empleado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Régimen
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Promedio 6M
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gratificación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bonificación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Remun.
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        const data = getCurrentDetalleData();
                        const totales = data.reduce((acc, grat) => {
                          const calcTabla = (grat.sueldos_mensuales?.length > 0)
                            ? calculeValoresReporte(grat)
                            : { gratBruta: grat.gratificacionBruta ?? 0, bonif: grat.bonificacion ?? 0, totalPagar: grat.gratificacionNeta ?? 0 };
                          acc.gratBruta += calcTabla.gratBruta;
                          acc.bonif += calcTabla.bonif;
                          acc.totalPagar += calcTabla.totalPagar;
                          return acc;
                        }, { gratBruta: 0, bonif: 0, totalPagar: 0 });
                        return (
                          <>
                      {data.map((grat) => {
                        const estadoConfig = getEstadoBadge(grat.estado);
                        const calcTabla = (grat.sueldos_mensuales?.length > 0)
                          ? calculeValoresReporte(grat)
                          : { promedio: grat.promedio6Meses ?? 0, base: grat.base_remuneracion ?? 0, tipoRemuneracion: grat.tipo_remuneracion || 'variable', gratBruta: grat.gratificacionBruta ?? 0, bonif: grat.bonificacion ?? 0, totalPagar: grat.gratificacionNeta ?? 0 };
                        const tipoRemGrat = { esFija: calcTabla.tipoRemuneracion === 'fija', texto: calcTabla.tipoRemuneracion === 'fija' ? 'Fija' : 'Variable' };
                        return (
                          <tr key={grat.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                                  <span className="text-white font-bold text-sm">
                                    {grat.empleado.split(' ')[0].charAt(0)}{grat.empleado.split(' ')[1]?.charAt(0)}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{grat.empleado}</div>
                                  <div className="text-xs text-gray-500">{grat.cargo}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {grat.regimen}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatCurrency(calcTabla.promedio)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                              {formatCurrency(calcTabla.gratBruta)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(calcTabla.bonif)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                              {formatCurrency(calcTabla.totalPagar)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tipoRemGrat.esFija ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>
                                {tipoRemGrat.esFija ? 'Fija' : 'Variable'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                                <span className="mr-1">{estadoConfig.icon}</span>
                                {estadoConfig.text}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <button
                                onClick={() => setModalVerDetalle(grat)}
                                className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                title="Ver detalle de cálculo"
                              >
                                Ver detalle
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                        <td colSpan={2} className="px-6 py-4 text-sm text-gray-900">Total</td>
                        <td className="px-6 py-4 text-sm text-gray-500">—</td>
                        <td className="px-6 py-4 text-sm font-medium text-green-600">{formatCurrency(totales.gratBruta)}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(totales.bonif)}</td>
                        <td className="px-6 py-4 text-sm font-bold text-blue-600">{formatCurrency(totales.totalPagar)}</td>
                        <td colSpan={3} className="px-6 py-4" />
                      </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden">
                  <div className="divide-y divide-gray-200">
                  {getCurrentDetalleData().map((grat) => {
                    const estadoConfig = getEstadoBadge(grat.estado);
                    const calcTabla = (grat.sueldos_mensuales?.length > 0)
                      ? calculeValoresReporte(grat)
                      : { promedio: grat.promedio6Meses ?? 0, base: grat.base_remuneracion ?? 0, tipoRemuneracion: grat.tipo_remuneracion || 'variable', gratBruta: grat.gratificacionBruta ?? 0, bonif: grat.bonificacion ?? 0, totalPagar: grat.gratificacionNeta ?? 0 };
                    const tipoRemGrat = { esFija: calcTabla.tipoRemuneracion === 'fija', texto: calcTabla.tipoRemuneracion === 'fija' ? 'Fija' : 'Variable' };
                    return (
                      <div key={grat.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {grat.empleado.split(' ')[0].charAt(0)}{grat.empleado.split(' ')[1]?.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{grat.empleado}</div>
                              <div className="text-xs text-gray-500">{grat.cargo}</div>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                            <span className="mr-1">{estadoConfig.icon}</span>
                            {estadoConfig.text}
                          </span>
                        </div>
                        
                        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Régimen:</span>
                            <div className="font-medium text-gray-900">{grat.regimen}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Remun.:</span>
                            <div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tipoRemGrat.esFija ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>
                                {tipoRemGrat.esFija ? 'Fija' : 'Variable'}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Promedio 6M:</span>
                            <div className="font-medium text-gray-900">{formatCurrency(calcTabla.promedio)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Gratificación:</span>
                            <div className="font-medium text-green-600">{formatCurrency(calcTabla.gratBruta)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Total:</span>
                            <div className="font-bold text-blue-600">{formatCurrency(calcTabla.totalPagar)}</div>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            onClick={() => setModalVerDetalle(grat)}
                            className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                          >
                            Ver detalle
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                  {getCurrentDetalleData().length > 0 && (() => {
                    const totales = getCurrentDetalleData().reduce((acc, grat) => {
                      const calcTabla = (grat.sueldos_mensuales?.length > 0)
                        ? calculeValoresReporte(grat)
                        : { gratBruta: grat.gratificacionBruta ?? 0, bonif: grat.bonificacion ?? 0, totalPagar: grat.gratificacionNeta ?? 0 };
                      acc.gratBruta += calcTabla.gratBruta;
                      acc.bonif += calcTabla.bonif;
                      acc.totalPagar += calcTabla.totalPagar;
                      return acc;
                    }, { gratBruta: 0, bonif: 0, totalPagar: 0 });
                    return (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Total</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Gratificación:</span>
                            <div className="font-medium text-green-600">{formatCurrency(totales.gratBruta)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Bonificación:</span>
                            <div className="font-medium text-gray-900">{formatCurrency(totales.bonif)}</div>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-500">Total a pagar:</span>
                            <div className="font-bold text-blue-600 text-base">{formatCurrency(totales.totalPagar)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </div>

          {/* Modal Ver detalle - últimos sueldos y cálculo */}
          {modalVerDetalle && (
            <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
              <div className="flex min-h-screen items-center justify-center p-4">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setModalVerDetalle(null)} />
                <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-4">
                      <h3 className="text-lg font-bold text-gray-900" id="modal-title">
                        Ver detalle - {modalVerDetalle.empleado}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setModalVerDetalle(null)}
                        className="text-gray-400 hover:text-gray-600 rounded-lg p-1"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{modalVerDetalle.cargo}</p>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{modalVerDetalle.regimen}</span>
                    </p>
                    {modalVerDetalle.ultima_fecha_ingreso_labores && (
                      <p className="text-xs text-gray-600 mb-4">
                        Última fecha de inicio de labores: <strong>{formatDateToDDMMYYYY(modalVerDetalle.ultima_fecha_ingreso_labores)}</strong>
                      </p>
                    )}

                    {(() => {
                      const sueldosIncluidos = getSueldosIncluidosParaCalculo();
                      const sueldosPeriodo =
                        sueldosIncluidos.length > 0
                          ? sueldosIncluidos.map((item) => ({ mes: item.mes, monto: getTotalMes(item) }))
                          : getUltimosSueldosMock(modalVerDetalle.promedio6Meses);
                      const tipoRem = getTipoRemuneracion(sueldosPeriodo);
                      return (
                        <div className={`rounded-lg p-4 mb-4 border ${tipoRem.esFija ? 'bg-emerald-50 border-emerald-200' : 'bg-sky-50 border-sky-200'}`}>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            Estado: <span className={tipoRem.esFija ? 'text-emerald-700 font-semibold' : 'text-sky-700 font-semibold'}>{tipoRem.texto}</span>
                          </p>
                          <p className="text-xs text-gray-700 mb-1">{tipoRem.detalle}</p>
                          <p className="text-sm text-gray-800">
                            Base de remuneración: <strong>{formatCurrency(tipoRem.baseRemuneracion)}</strong>
                            {tipoRem.esFija ? ' (último sueldo)' : ' (promedio del período)'}
                          </p>
                        </div>
                      );
                    })()}

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Últimos sueldos del período</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Haga clic en un mes para ver los ingresos. Los meses <strong>sin planilla cerrada</strong> aparecen en azul:
                        escriba cuánto ganó y pulse <strong>Guardar configuración</strong>. Use la <strong>✕</strong> para excluir un mes de la base.
                      </p>
                      {ingresosPorMes.some((item) => item.requiereMontoManual && getTotalMes(item) <= 0) && (
                        <p className="text-xs text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5 mb-3">
                          Hay meses computables sin planilla. Indique el monto ganado en cada uno para que entren al promedio y a la proporcionalidad (X/6).
                        </p>
                      )}
                      {ingresosPorMes.length === 0 ? (
                        <div className="text-sm text-gray-500 py-4">Cargando desglose por mes...</div>
                      ) : (
                        <>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {ingresosPorMes.map((item) => {
                              const total = getTotalMes(item);
                              const activo = mesIngresosAbierto === item.mes;
                              const esEditableManual = item.requiereMontoManual || total === 0;
                              const excluido = mesesExcluidosDelCalculo.has(item.mes);
                              return (
                                <div
                                  key={item.mes}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setMesIngresosAbierto((m) => (m === item.mes ? null : item.mes))}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMesIngresosAbierto((m) => (m === item.mes ? null : item.mes)); } }}
                                  className={`inline-flex items-center gap-2 pl-3 pr-1 py-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer ${
                                    excluido
                                      ? 'bg-gray-100 border-gray-300 text-gray-500 opacity-75'
                                      : item.requiereMontoManual && total <= 0
                                        ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                                      : item.pendienteCierre
                                        ? 'bg-amber-50 border-amber-300 text-amber-900'
                                        : activo
                                          ? 'bg-green-100 border-green-400 text-green-900 ring-1 ring-green-400'
                                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  } ${excluido ? 'line-through' : ''}`}
                                >
                                  <span className="font-medium">{item.mes}</span>
                                  {esEditableManual ? (
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      placeholder="0.00"
                                      value={item.montoEditado ?? (total > 0 ? total : '')}
                                      onChange={(e) => updateMontoMes(item.mes, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-24 px-2 py-0.5 text-right border border-gray-300 rounded text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                    />
                                  ) : (
                                    <span className="font-semibold">{formatCurrency(total)}</span>
                                  )}
                                  {item.requiereMontoManual && total <= 0 && !excluido && (
                                    <span className="text-xs font-normal text-indigo-700">(sin planilla)</span>
                                  )}
                                  {item.pendienteCierre && !excluido && (
                                    <span className="text-xs font-normal text-amber-700">(prom. Jul-Nov)</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMesExcluidoDelCalculo(item.mes);
                                    }}
                                    className={`ml-0.5 p-0.5 rounded hover:bg-gray-300/80 focus:outline-none focus:ring-1 focus:ring-gray-400 ${excluido ? 'text-gray-500' : 'text-gray-400 hover:text-red-600'}`}
                                    title={excluido ? 'Incluir este mes en el cálculo' : 'Excluir este mes del cálculo'}
                                    aria-label={excluido ? 'Incluir mes' : 'Excluir mes'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          {mesIngresosAbierto && (() => {
                            const item = ingresosPorMes.find((i) => i.mes === mesIngresosAbierto);
                            if (!item) return null;
                            return (
                              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50/50">
                                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-800">
                                  Ingresos de {item.mes}
                                  {item.esConceptosPlame && (
                                    <span className="ml-2 text-xs font-normal text-gray-600">(conceptos PLAME de planilla cerrada)</span>
                                  )}
                                  {item.conceptosOrigen === 'sin_desglose' && (
                                    <span className="ml-2 text-xs font-normal text-gray-500">(sin desglose en planilla)</span>
                                  )}
                                  {item.conceptosOrigen === 'manual' && (
                                    <span className="ml-2 text-xs font-normal text-indigo-700">(monto manual — sin planilla cerrada)</span>
                                  )}
                                  {item.pendienteCierre && item.leyenda && (
                                    <span className="ml-2 text-xs font-normal text-amber-700">— {item.leyenda}</span>
                                  )}
                                </div>
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-12">Incluir</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {item.conceptos.map((c) => (
                                      <tr key={c.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2">
                                          <input
                                            type="checkbox"
                                            checked={!!c.incluir}
                                            onChange={() => toggleConceptoIncluir(item.mes, c.id)}
                                            className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                          />
                                        </td>
                                        <td className="px-4 py-2 text-gray-900">{c.nombre}</td>
                                        <td className="px-4 py-2 text-right font-medium text-gray-900">{formatCurrency(c.monto)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 text-sm text-right">
                                  <span className="text-gray-600">Total del mes (conceptos incluidos): </span>
                                  <span className="font-semibold text-gray-900">{formatCurrency(getTotalMes(item))}</span>
                                </div>
                              </div>
                            );
                          })()}
                          {esPeriodoDiciembre && (
                            <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                              La gratificación de noviembre se calcula antes de cerrar la planilla de diciembre; por tanto, para Diciembre se usa el promedio de Julio a Noviembre.
                            </p>
                          )}
                          {(() => {
                            const sueldosParaBase = getSueldosIncluidosParaCalculo()
                              .map((item) => ({ mes: item.mes, monto: getTotalMes(item) }));
                            const tipo = getTipoRemuneracion(sueldosParaBase);
                            const cantMeses = sueldosParaBase.length;
                            return (
                              <p className="text-xs text-gray-500 mt-2">
                                {tipo.esFija
                                  ? <>Base = último sueldo = {formatCurrency(tipo.baseRemuneracion)} (remuneración fija)</>
                                  : <>Base = promedio del período = {formatCurrency(tipo.baseRemuneracion)} (remuneración variable)</>}
                                {cantMeses > 0 && cantMeses < 6 && (
                                  <> • Gratificación proporcional: {cantMeses} de 6 meses</>
                                )}
                              </p>
                            );
                          })()}
                        </>
                      )}
                    </div>

                    {(() => {
                      const sueldosIncluidos = getSueldosIncluidosParaCalculo();
                      const sueldosParaCalculo = sueldosIncluidos.map((item) => ({ mes: item.mes, monto: getTotalMes(item) }));
                      const tipoRem = getTipoRemuneracion(sueldosParaCalculo);
                      const mesesPeriodo = 6; // Semestral: Enero-Jun o Julio-Dic
                      const factorMeses = sueldosIncluidos.length > 0 ? sueldosIncluidos.length / mesesPeriodo : 0;
                      let gratBruta = Math.round(tipoRem.baseRemuneracion * factorMeses * 100) / 100;
                      const esPequenaEmpresa = esRegimenMypePequenaEmpresa(modalVerDetalle.regimen_codigo, modalVerDetalle.regimen);
                      if (esPequenaEmpresa) gratBruta = Math.round(gratBruta * 0.5 * 100) / 100;
                      const bonif = Math.round(gratBruta * 0.09 * 100) / 100;
                      const totalPagar = Math.round((gratBruta + bonif) * 100) / 100;
                      return (
                        <div className="border-t border-gray-200 pt-4">
                          {esPequenaEmpresa && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3">
                              ℹ️ Régimen MYPE Pequeña Empresa: se está calculando a la mitad (50%).
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-500">Gratificación bruta</span>
                              <div className="font-semibold text-green-600">{formatCurrency(gratBruta)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Bonificación (9%)</span>
                              <div className="font-semibold text-gray-900">{formatCurrency(bonif)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Total a pagar</span>
                              <div className="font-bold text-blue-600">{formatCurrency(totalPagar)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Días trabajados</span>
                              <div className="font-medium text-gray-900">{modalVerDetalle.diasTrabajados ?? '-'}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleDescargarConstanciaIndividual(modalVerDetalle)}
                        disabled={generandoConstanciaIndividual || !modalVerDetalle}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm disabled:opacity-50"
                        title="Constancia de pago de gratificación con firma de gerencia"
                      >
                        {generandoConstanciaIndividual ? 'Generando…' : 'Constancia PDF'}
                      </button>
                      {gratificacionId && !gratificacionCerrada && (
                        <button
                          type="button"
                          onClick={handleGuardarConfig}
                          disabled={guardandoConfig}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {guardandoConfig ? 'Guardando...' : 'Guardar configuración'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setModalVerDetalle(null)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <CerrarGratificacionConfirmModal
            open={showModalCerrar}
            subtitulo={
              selectedPeriodo
                ? `Gratificación ${selectedPeriodo.periodo || ''} ${selectedPeriodo.año || ''}`.trim()
                : null
            }
            submitting={cerrandoGratificacion}
            onCancel={() => !cerrandoGratificacion && setShowModalCerrar(false)}
            onConfirm={handleConfirmarCerrarGratificacion}
          />
        </>
      )}
    </div>
  );
};

export default GratificacionesDetalle;
